/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <3rd-party library> */
import { noop } from 'lodash';
import { GlobalWorkerOptions, getDocument, type PDFDocumentLoadingTask, PixelsPerInch, version } from 'pdfjs-dist';
import { type PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import {
  memo,
  type ReactNode,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { Spinner } from '@air/primitive-spinner';
import { useBreakpointsContext } from '@air/provider-media-query';
import { type ReportErrorToBugsnagParams } from '@air/utils-error';

import {
  PDF_LIST_SCROLL_MARGIN,
  PDFPagesVirtualizedList,
  type PDFPagesVirtualizedListProps,
  type PDFVirtualizedListHandle,
} from './components/PDFPagesVirtualizedList';
import { type RenderOutlinePageMenu } from './components/PDFViewerOutline/components/PDFViewerOutlinePage/PDFViewerOutlinePage';
import { PDF_OUTLINE_WIDTH, PDFViewerOutline } from './components/PDFViewerOutline/PDFViewerOutline';
import { PDFViewerPage, type RenderPDFViewerPageLayers } from './components/PDFViewerPage';
import { ScrollToCurrentPDFResultObserver } from './components/ScrollToCurrentPDFResultObserver';
import { PDFCurrentSearchResultElementContextProvider } from './contexts/PDFCurrentSearchResultElementContext';
import { type PDFViewerContextType, PDFViewerControlProvider } from './contexts/PDFViewerControlContext';
import { PDFViewerSearchContextProvider } from './contexts/PDFViewerSearch/PDFViewerSearchContext';
import { useGetPDFPageHeight } from './hooks/useGetPDFPageHeight';
import { useMouseWheelZoom } from './hooks/useMouseWheelZoom';
import { usePDFTextSelectionListener } from './hooks/usePDFTextSelectionListener';
import { usePersistScrollPositionOnZoom } from './hooks/usePersistScrollPositionOnZoom';
import { pdfScaleSteps } from './utils/constants';
import { findIndexOfLowestClosestNumber } from './utils/findIndexOfLowestClosestNumber';
import { findScaleForDimensions } from './utils/findScaleForDimensions';
import { PDFPagesCache } from './utils/PDFPagesCache';
import { readRef } from './utils/readRef';

/** The ref is empty until the document loads, so consumers only ever receive one that is set. */
const hasLoadedCache = (ref: RefObject<PDFPagesCache | undefined>): ref is RefObject<PDFPagesCache> =>
  ref.current !== undefined;

import { PDFLinkService } from './utils/PdfLinkService';

export type PDFViewerHandle = {
  scrollToPage: (pageNumber: number) => void;
};

// PDF controls interface for render props
export type PDFViewerControls = {
  scale: number;
  updateScale: (scale: number) => void;
  isOutlineOpen: boolean;
  setIsOutlineOpen: (isOpen: boolean) => void;
  currentPage: number;
  numPages: number;
  changePage: (pageNumber: number) => void;
  pdfDocument: PDFDocumentProxy | null;
  pdfPagesCacheRef: RefObject<PDFPagesCache> | null;
  renderOutlinePageMenu?: RenderOutlinePageMenu;
};

export type PDFViewerProps = {
  pdfData: ArrayBuffer | null;
  handleRef?: Ref<PDFViewerHandle>;
  children: (controls: PDFViewerControls) => ReactNode;
  renderOutlinePageMenu?: RenderOutlinePageMenu;
  renderPageLayers?: RenderPDFViewerPageLayers;
  onPDFLoaded?: (pdfDocument: PDFDocumentProxy) => void;
  errorView: ReactNode;
  onError?: (params: ReportErrorToBugsnagParams) => void;
  hideInternalOutline?: boolean;
  /**
   * When false, PDF annotation anchors are not given an href (internal page
   * destinations still work via `goToDestination`). Required so callers must opt
   * in explicitly — free-plan public surfaces fail closed when omitted.
   */
  areExternalLinksEnabled: boolean;
};

GlobalWorkerOptions.workerSrc = `/pdf-js/${version}/pdf.worker.min.mjs`;
const cMapUrl = `https://unpkg.com/pdfjs-dist@${version}/cmaps/`;
const standardFontsUrl = `https://unpkg.com/pdfjs-dist@${version}/standard_fonts/`;

const DEFAULT_SCALE = 1;
const DEFAULT_SCALE_VALUE = 0.75;
const DEFAULT_SCALE_INDEX = pdfScaleSteps.indexOf(DEFAULT_SCALE_VALUE) ?? 0;

export const PDFViewer = memo(
  ({
    pdfData,
    children,
    renderOutlinePageMenu,
    renderPageLayers,
    handleRef,
    onPDFLoaded,
    errorView,
    onError,
    hideInternalOutline,
    areExternalLinksEnabled,
  }: PDFViewerProps) => {
    const pdfPagesCacheRef = useRef<PDFPagesCache>();

    const listRef = useRef<PDFVirtualizedListHandle>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    usePDFTextSelectionListener();

    const onPDFLoadedRef = useRef(onPDFLoaded);
    onPDFLoadedRef.current = onPDFLoaded;

    // Inline scale management
    const [scaleStep, setScaleStep] = useState(DEFAULT_SCALE_INDEX);
    const currentScale = pdfScaleSteps[scaleStep];
    const [_, startTransition] = useTransition();

    const updateScale = useCallback((newScale: number) => {
      startTransition(() => {
        const newScaleIndex = findIndexOfLowestClosestNumber(pdfScaleSteps, newScale);
        setScaleStep(newScaleIndex);
      });
    }, []);

    const zoomIn = useCallback(() => {
      startTransition(() => setScaleStep((prev) => (prev < pdfScaleSteps.length - 1 ? prev + 1 : prev)));
    }, []);

    const zoomOut = useCallback(() => {
      startTransition(() => setScaleStep((prev) => (prev > 0 ? prev - 1 : prev)));
    }, []);

    const setScaleForDimensions = useCallback((scaleToSet: number) => {
      const scaleIndex = findIndexOfLowestClosestNumber(pdfScaleSteps, scaleToSet);
      setScaleStep(scaleIndex);
    }, []);

    const [pdfDocument, setPDFDocument] = useState<PDFDocumentProxy | null>();
    const [currentPage, setCurrentPage] = useState(1);
    const [isOutlineOpen, setIsOutlineOpen] = useState(true);

    const { isAboveLargeScreen } = useBreakpointsContext();

    const pdfLoadingTaskRef = useRef<PDFDocumentLoadingTask | null>();

    useMouseWheelZoom({ zoomIn, zoomOut });

    usePersistScrollPositionOnZoom({ currentPage, currentScale, listRef });

    useImperativeHandle(handleRef, () => ({
      scrollToPage: (pageNumber) => {
        listRef.current?.scrollToIndex(pageNumber - 1);
      },
    }));

    useEffect(() => {
      setPDFDocument(null);
    }, [pdfData]);

    useEffect(() => {
      if (pdfDocument && listRef.current) {
        requestAnimationFrame(() => onPDFLoadedRef.current?.(pdfDocument));
      }
    }, [pdfDocument]);

    const linkService = useMemo(() => {
      if (!pdfDocument) {
        return undefined;
      }

      return new PDFLinkService({
        externalLinkEnabled: areExternalLinksEnabled,
        pdfDocument,
        pdfViewer: {
          scrollToPage: (param) => {
            // Handle both direct number and object parameter formats
            const pageNumber = typeof param === 'number' ? param : param?.pageNumber;

            if (pageNumber) {
              listRef.current?.scrollToIndex(pageNumber - 1);
            }
          },
        },
      });
    }, [areExternalLinksEnabled, pdfDocument]);

    const isOutliveVisibleRef = useRef(isOutlineOpen);
    isOutliveVisibleRef.current = isOutlineOpen;

    const onAllPagesLoadInCache = useCallback(() => {
      listRef.current?.updateItemsSize();
    }, []);

    const onAllPagesLoadInCacheRef = useRef(onAllPagesLoadInCache);
    onAllPagesLoadInCacheRef.current = onAllPagesLoadInCache;

    useIsomorphicLayoutEffect(() => {
      const loadPDF = async (pdfData: ArrayBuffer) => {
        if (pdfLoadingTaskRef.current) {
          await pdfLoadingTaskRef.current.destroy();
          pdfLoadingTaskRef.current = null;
        }

        pdfLoadingTaskRef.current = getDocument({
          cMapUrl,
          // pdf.js transfers "data" to worker and loses control on it.
          // we want to pass copy of "data" to getDocument to avoid crashes when PDFViewer rerenders
          data: pdfData.slice(0),
          isOffscreenCanvasSupported: true,
          standardFontDataUrl: standardFontsUrl,
        });

        pdfLoadingTaskRef.current.promise.then(
          async (pdfDocument) => {
            pdfPagesCacheRef.current = new PDFPagesCache(pdfDocument, onAllPagesLoadInCacheRef.current);

            const container = readRef(containerRef);

            if (!container) {
              return;
            }
            const firstPage = await pdfDocument.getPage(1);
            const firstPageViewport = firstPage.getViewport({ scale: PixelsPerInch.PDF_TO_CSS_UNITS });

            const availableWidth = container.clientWidth - (isOutliveVisibleRef.current ? PDF_OUTLINE_WIDTH : 0);
            const availableHeight = container.clientHeight;

            const scaleToSet = findScaleForDimensions({
              containerSize: { height: availableHeight - PDF_LIST_SCROLL_MARGIN * 2, width: availableWidth },
              defaultScale: DEFAULT_SCALE,
              elementSize: firstPageViewport,
            });

            setScaleForDimensions(scaleToSet);
            setPDFDocument(pdfDocument);
          },
          (error) => {
            onErrorRef.current?.({ context: 'Failed to load pdf', error });
          },
        );
      };

      if (pdfData) {
        void loadPDF(pdfData);
      }
    }, [pdfData, setScaleForDimensions]);

    const { getPageHeight } = useGetPDFPageHeight({ currentScale, pdfDocument, pdfPagesCacheRef });

    const pageRowRenderer = useCallback<PDFPagesVirtualizedListProps['children']>(
      ({ index }) => {
        if (!linkService || !pdfPagesCacheRef) {
          return null;
        }

        return (
          <PDFViewerPage
            cachedHeight={getPageHeight(index)}
            linkService={linkService}
            pageNumber={index + 1}
            pdfPageCacheRef={pdfPagesCacheRef}
            renderPageLayers={renderPageLayers}
            scale={pdfScaleSteps[scaleStep]}
          />
        );
      },
      [getPageHeight, linkService, renderPageLayers, scaleStep],
    );

    const onTopVisibleElementChange = useCallback<PDFPagesVirtualizedListProps['onTopVisibleElementChange']>(
      (index) => {
        setCurrentPage(index + 1);
      },
      [setCurrentPage],
    );

    const changePage = useCallback<PDFViewerContextType['changePage']>(
      (pageNumber) => {
        listRef.current?.scrollToIndex(pageNumber - 1);
      },
      [listRef],
    );

    // Create PDF controls object for render props
    const pdfControls: PDFViewerControls = useMemo(
      () => ({
        changePage,
        currentPage,
        isOutlineOpen,
        numPages: pdfDocument?.numPages ?? 0,
        pdfDocument: pdfDocument ?? null,
        pdfPagesCacheRef: hasLoadedCache(pdfPagesCacheRef) ? pdfPagesCacheRef : null,
        renderOutlinePageMenu,
        scale: currentScale,
        setIsOutlineOpen,
        updateScale,
      }),
      [
        currentScale,
        updateScale,
        isOutlineOpen,
        setIsOutlineOpen,
        currentPage,
        pdfDocument,
        changePage,
        renderOutlinePageMenu,
      ],
    );

    if (!pdfDocument) {
      return (
        <div className="flex size-full items-center justify-center" ref={containerRef}>
          <PDFViewerControlProvider
            changePage={noop}
            currentPage={1}
            isOutlineOpen={false}
            numPages={0}
            scale={currentScale}
            setIsOutlineOpen={setIsOutlineOpen}
            updateScale={updateScale}
          >
            <PDFViewerSearchContextProvider pdfDocument={null} pdfPagesCache={null}>
              {children(pdfControls)}
            </PDFViewerSearchContextProvider>
          </PDFViewerControlProvider>
          {errorView ? errorView : <Spinner className="size-8 ads:text-ads-cyan-10 text-teal-9" />}
        </div>
      );
    }

    const isOutlineVisible = !hideInternalOutline && isOutlineOpen && isAboveLargeScreen;

    return (
      <div className="pdfViewer relative flex size-full flex-col ads:bg-base bg-gray-1" ref={containerRef}>
        {!hideInternalOutline && (
          <PDFViewerOutline
            currentPage={currentPage}
            isOpen={isOutlineVisible}
            onPageClick={changePage}
            pagesCount={pdfDocument.numPages}
            pdfPageCacheRef={pdfPagesCacheRef}
            renderOutlinePageMenu={renderOutlinePageMenu}
          />
        )}
        <div
          className="relative flex h-full"
          style={{
            marginLeft: isOutlineVisible ? PDF_OUTLINE_WIDTH : 0,
            width: isOutlineVisible ? `calc(100% - ${PDF_OUTLINE_WIDTH}px)` : '100%',
          }}
        >
          <PDFViewerControlProvider
            changePage={changePage}
            currentPage={currentPage}
            isOutlineOpen={isOutlineVisible}
            numPages={pdfDocument.numPages}
            scale={currentScale}
            setIsOutlineOpen={setIsOutlineOpen}
            updateScale={updateScale}
          >
            <PDFViewerSearchContextProvider pdfDocument={pdfDocument} pdfPagesCache={pdfPagesCacheRef.current ?? null}>
              <PDFCurrentSearchResultElementContextProvider>
                <PDFPagesVirtualizedList
                  getPageHeight={getPageHeight}
                  onTopVisibleElementChange={onTopVisibleElementChange}
                  ref={listRef}
                  rowsCount={pdfDocument.numPages}
                >
                  {pageRowRenderer}
                </PDFPagesVirtualizedList>

                {children(pdfControls)}
                <ScrollToCurrentPDFResultObserver currentPageNumber={currentPage} listRef={listRef} />
              </PDFCurrentSearchResultElementContextProvider>
            </PDFViewerSearchContextProvider>
          </PDFViewerControlProvider>
        </div>
      </div>
    );
  },
);

PDFViewer.displayName = 'PDFViewer';
